import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, HostListener, effect, inject, input, output, signal, untracked } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { catchError, EMPTY, finalize, map, of, Subject, switchMap, timer } from 'rxjs';

import {
    HistoricoCapitalSocialResponse,
    HistoricoCertificadoResponse,
    PrestadorCrmDisponivelResponse,
    PrestadorRenovacaoCertificadoRequest,
    PrestadorDadosGeraisOpcaoResponse,
    PrestadorDadosGeraisReferenciasResponse,
    PrestadorDadosGeraisRequest,
    PrestadorDadosGeraisResponse,
    PrestadorFilialResponse
} from '@/core/api/sicom-api/registro/prestador/prestador-dados-gerais.model';
import { PrestadorDadosGeraisService } from '@/core/api/sicom-api/registro/prestador/prestador-dados-gerais.service';
import { PrestadorAlertasResponse } from '@/core/api/sicom-api/registro/prestador/prestador-alertas.model';
import { PrestadorAlertasService } from '@/core/api/sicom-api/registro/prestador/prestador-alertas.service';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorAberturaNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-abertura-navigation.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { FormDatePicker } from '@/shared/components/layout/form-date-picker/form-date-picker';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import { AlterarSituacao } from './alterar-situacao/alterar-situacao';
import { ProrrogarPrazo } from './prorrogar-prazo/prorrogar-prazo';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { ValueUtils } from '@/shared/utils/value-utils';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { certificadoStatusView } from '@/shared/models/certificado-status.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
const CRM_VERIFICACAO_DEBOUNCE_MS = 400;
const TIPO_INSCRICAO_PROVISORIA = 2;
const SITUACAO_PROVISORIA_PENDENTE = 2;

type AlertaPrestador = {
    chave: keyof PrestadorAlertasResponse;
    mensagem: string;
};

// Mensagens pt-BR de cada flag informativa (fiéis às verificações do legado UntDmPrestador.pas).
const ALERTAS_PRESTADOR: AlertaPrestador[] = [
    { chave: 'crmDuplicado', mensagem: 'CRM duplicado: existe outro prestador com o mesmo CRM nesta UF.' },
    { chave: 'diretorTecnicoDuplicado', mensagem: 'Existe mais de um Diretor Técnico ativo cadastrado. Selecione o correto!' },
    { chave: 'diretorClinicoDuplicado', mensagem: 'Existe mais de um Diretor Clínico ativo cadastrado. Selecione o correto!' },
    { chave: 'prestadorInadimplente', mensagem: 'Prestador inadimplente: existe anuidade em aberto.' },
    { chave: 'corpoClinicoInadimplente', mensagem: 'Há médico do corpo clínico com anuidade em aberto.' },
    { chave: 'especialidadePendenteDiretorTecnico', mensagem: 'O Diretor Técnico ativo não possui a especialidade única do prestador.' }
];

type OpcaoSelect = {
    label: string;
    value: string;
    group?: string | null;
    parent?: string | null;
};

@Component({
    selector: 'app-dados-gerais-prestador',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        ReactiveFormsModule,
        CardContent,
        InputText,
        Select,
        InputNumber,
        FormDatePicker,
        BotaoAcoes,
        TooltipModule,
        Button,
        Dialog,
        ConfirmDialog,
        TableModule,
        PesquisarPessoaCustomComponent,
        AlterarSituacao,
        ProrrogarPrazo,
        Tag,
        CurrencyPipe,
        DatePipe,
        CanDirective
    ],
    providers: [ConfirmationService],
    templateUrl: './dados-gerais-prestador.html',
    styleUrl: './dados-gerais-prestador.scss'
})
export class DadosGeraisPrestador {
    pessoa = input<Pessoa>();
    /** Emitido após salvar os dados gerais, para o card de pessoa ser recarregado. */
    readonly salvo = output<void>();
    protected readonly rolePrestadoresDadosGeraisEditar = PERMISSOES.prestadores.dadosGerais.editar;
    protected readonly EInconPos = EInconPos;

    /**
     * Salvar e Cancelar saíram do menu: são as ações mais frequentes da tela e viravam duas
     * etapas — abrir o popover e procurar o item. Como destaque, o `app-botao-acoes` as desenha
     * como botão ao lado de "Ações" no desktop e como FAB na linha no mobile.
     */
    get itensEdicao(): AppMenuItem[] {
        const role = this.rolePrestadoresDadosGeraisEditar;
        return [
            acaoMenuItem({
                id: 'salvar',
                label: 'Salvar',
                icon: 'pi pi-check',
                cor: 'salvar',
                disabled: !this.form.valid || this.carregando(),
                permission: role,
                command: () => this.saveChanges()
            }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelEditing() })
        ];
    }

    get itensAcoes(): AppMenuItem[] {
        const role = this.rolePrestadoresDadosGeraisEditar;
        const editando = this.isEditing;
        return [
            acaoMenuItem({ id: 'editar', label: 'Editar', icon: 'pi pi-pencil', cor: 'editar', disabled: editando, permission: role, command: () => this.enableEditing() }),
            // Vinha do header do sub-card "Inscricao".
            acaoMenuItem({
                id: 'prorrogar',
                label: 'Prorrogar inscrição',
                icon: 'pi pi-calendar-plus',
                cor: 'neutro',
                disabled: !this.podeProrrogar,
                permission: role,
                command: () => this.abrirProrrogarPrazo()
            }),
            // Vinha do header do sub-card "Certificado" (era um <i> sem rotulo).
            acaoMenuItem({ id: 'historico-certificado', label: 'Histórico do certificado', icon: 'pi pi-history', cor: 'info', command: () => this.abrirHistoricoCertificado() })
        ];
    }

    private readonly destroyRef = inject(DestroyRef);
    private readonly aberturaNavigationService = inject(PrestadorAberturaNavigationService);
    private readonly camposEditaveis = [
        'crm',
        'razaoSocial',
        'nomeFantasia',
        'nomeUsual',
        'modalidade',
        'idTipoInscricao',
        'idSituacao',
        'dataInscricao',
        'dataSituacao',
        'filtroClassificacao',
        'idClassificacao',
        'classificacaoDetalhe',
        'idClassificacaoSubtipo',
        'categoria',
        'tipoEstabelecimento',
        'capitalSocial',
        'capitalSocialAtualizacao',
        'capitalSocialOutraUf',
        'capitalSocialOutraUfAtualizacao',
        'numeroFiliais',
        'naturezaJuridicaMantenedor'
    ] as const;

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        crm: [{ value: '', disabled: true }, [Validators.pattern(/^\d{0,7}$/)], [this.crmDisponibilidadeValidator()]],
        uf: [{ value: '', disabled: true }],
        cls: [{ value: '', disabled: true }],
        razaoSocial: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(80)]],
        dataInscricao: this.fb.control<Date | null>({ value: null, disabled: true }, Validators.required),
        nomeFantasia: [{ value: '', disabled: true }, Validators.maxLength(80)],
        nomeUsual: [{ value: '', disabled: true }, Validators.maxLength(80)],
        modalidade: this.fb.control<string | null>({ value: null, disabled: true }, Validators.required),
        idTipoInscricao: this.fb.control<string | null>({ value: null, disabled: true }, Validators.required),
        idSituacao: this.fb.control<string | null>({ value: null, disabled: true }, Validators.required),
        dataSituacao: this.fb.control<Date | null>({ value: null, disabled: true }),
        filtroClassificacao: this.fb.control<number | null>({ value: 0, disabled: true }),
        idClassificacao: this.fb.control<string | null>({ value: null, disabled: true }),
        classificacaoDetalhe: this.fb.control<string | null>({ value: null, disabled: true }),
        idClassificacaoSubtipo: this.fb.control<string | null>({ value: null, disabled: true }),
        especialidadeEnfase: [{ value: '', disabled: true }],
        categoria: this.fb.control<string | null>({ value: null, disabled: true }),
        dataRenovacao: this.fb.control<Date | null>({ value: null, disabled: true }),
        validadeCertificado: this.fb.control<Date | null>({ value: null, disabled: true }),
        tipoEstabelecimento: this.fb.control<string | null>({ value: null, disabled: true }),
        capitalSocial: this.fb.control<number | null>({ value: null, disabled: true }),
        capitalSocialAtualizacao: this.fb.control<Date | null>({ value: null, disabled: true }),
        capitalSocialOutraUf: this.fb.control<number | null>({ value: null, disabled: true }),
        capitalSocialOutraUfAtualizacao: this.fb.control<Date | null>({ value: null, disabled: true }),
        numeroFiliais: this.fb.control<number | null>({ value: null, disabled: true }),
        matrizCodigo: [{ value: '', disabled: true }],
        matrizUf: [{ value: '', disabled: true }],
        matrizDescricao: [{ value: '', disabled: true }],
        mantenedoraCodigo: this.fb.control<number | null>({ value: null, disabled: true }),
        mantenedoraUf: [{ value: '', disabled: true }],
        mantenedoraRazaoSocial: [{ value: '', disabled: true }],
        naturezaJuridicaMantenedor: this.fb.control<string | null>({ value: null, disabled: true })
    });
    readonly carregando = signal(false);
    readonly crmVerificando = signal(false);
    readonly crmDisponibilidade = signal<'disponivel' | 'indisponivel' | null>(null);
    readonly pesquisaMatrizVisivel = signal(false);
    readonly pesquisaMantenedoraVisivel = signal(false);
    readonly prorrogarPrazoVisivel = signal(false);
    readonly situacaoCascataVisivel = signal(false);
    readonly situacaoNovaCascata = signal<number | null>(null);
    readonly dataSituacaoCascata = signal<string | null>(null);
    readonly filiaisDialogVisivel = signal(false);
    readonly filiais = signal<PrestadorFilialResponse[]>([]);
    readonly carregandoFiliais = signal(false);
    readonly zerandoFiliais = signal(false);
    readonly alertas = signal<PrestadorAlertasResponse | null>(null);
    readonly capitalHistoricoDialogVisivel = signal(false);
    readonly capitalHistorico = signal<HistoricoCapitalSocialResponse[]>([]);
    readonly carregandoCapitalHistorico = signal(false);
    readonly certificadoHistoricoDialogVisivel = signal(false);
    readonly certificadoHistorico = signal<HistoricoCertificadoResponse[]>([]);
    readonly carregandoCertificadoHistorico = signal(false);
    readonly salvandoRenovacao = signal(false);
    readonly renovacaoForm = this.fb.group({
        dtRenovacao: this.fb.control<Date | null>(new Date(), Validators.required)
    });
    readonly capitalHistoricoTipo = signal<'P' | 'M'>('P');
    isEditing = false;
    isLabelResumo = false;

    labelsResumo: Record<string, Record<string, string>> = {
        tipoInscricao: {
            full: 'Tipo de Inscrição',
            resumo: 'Tp. Inscrição'
        }
    };

    opcoesModalidade: OpcaoSelect[] = [];
    opcoesTipoInscricao: OpcaoSelect[] = [];
    opcoesSituacao: OpcaoSelect[] = [];
    opcoesCategoria: OpcaoSelect[] = [];
    opcoesTipoEstabelecimento: OpcaoSelect[] = [];
    opcoesClassificacao: OpcaoSelect[] = [];
    opcoesClassificacaoDetalhe: OpcaoSelect[] = [];
    opcoesClassificacaoSubtipo: OpcaoSelect[] = [];

    // Filtro de visibilidade da classificacao (dbfiltroclassificacao do legado, UntDmPrestador.dfm:10696).
    // 0 = Todas; 1 = classificacoes com opcao de visibilidade NULL ("Novas"); 2 = com 'T'/'N' ("Transicao").
    readonly opcoesFiltroClassificacao: { value: number; label: string }[] = [
        { value: 0, label: 'Todas' },
        { value: 1, label: 'Novas Classificações' },
        { value: 2, label: 'Transição' }
    ];

    // "Natureza Jurídica" da Mantenedora (rgNaturezaMant, UntCadPrestador.dfm:1582): 'U' = Público, 'R' = Privado.
    readonly opcoesNaturezaJuridicaMantenedor: { value: string; label: string }[] = [
        { value: 'U', label: 'Público' },
        { value: 'R', label: 'Privado' }
    ];

    private dadosCarregados: PrestadorDadosGeraisResponse | null = null;

    /** Pedidos de recarga dos dados gerais. Ver a assinatura no construtor. */
    private readonly recarregar$ = new Subject<Pessoa>();

    constructor(
        private readonly prestadorDadosGeraisService: PrestadorDadosGeraisService,
        private readonly prestadorAlertasService: PrestadorAlertasService,
        private readonly notificacoesService: NotificacoesService,
        private readonly confirmationService: ConfirmationService
    ) {
        this.configurarRegrasFormulario();
        this.resumirLabels();

        // Recarga como fluxo: o `switchMap` descarta a busca anterior quando chega um pedido novo,
        // que e a semantica de "recarrega o estado atual". O guard de reentrancia que existia aqui
        // fazia o oposto — descartava o pedido NOVO —, entao trocar de prestador durante uma carga
        // deixava a tela com os dados do prestador anterior.
        this.recarregar$
            .pipe(
                switchMap((pessoa) => {
                    // `set(true)` fica aqui dentro: o switchMap desinscreve o inner anterior antes
                    // de projetar, e o `finalize` dele zeraria o sinal logo depois de um `tap`.
                    this.carregando.set(true);
                    this.carregarAlertas(pessoa);
                    return this.prestadorDadosGeraisService.buscar(pessoa).pipe(
                        finalize(() => this.carregando.set(false)),
                        // `catchError` no fluxo interno: no externo, o primeiro erro encerraria a
                        // assinatura e nenhuma recarga posterior voltaria a funcionar.
                        catchError(() => {
                            this.limparFormulario();
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((response) => {
                this.aplicarDados(response);
                this.isEditing = false;
            });

        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa?.uf) {
                this.limparFormulario();
                return;
            }

            untracked(() => this.carregarDados(pessoa));
        });
    }

    get crm(): AbstractControl | null {
        return this.form.get('crm');
    }

    get uf(): AbstractControl | null {
        return this.form.get('uf');
    }

    get crmBloqueadoPorRJ(): boolean {
        return this.form.get('uf')?.value === 'RJ';
    }

    get cls(): AbstractControl | null {
        return this.form.get('cls');
    }

    get dataInscricao(): AbstractControl | null {
        return this.form.get('dataInscricao');
    }

    get dataSituacao(): AbstractControl | null {
        return this.form.get('dataSituacao');
    }

    get dataRenovacao(): AbstractControl | null {
        return this.form.get('dataRenovacao');
    }

    get validadeCertificado(): AbstractControl | null {
        return this.form.get('validadeCertificado');
    }

    /** Apresentação do status do certificado para a tag do card "Certificado". */
    get certificadoTag() {
        return certificadoStatusView(this.dadosCarregados?.certificadoStatus);
    }

    /** True quando o certificado esta invalido/expirado — aplica o tom vermelho ao card. */
    get certificadoDanger(): boolean {
        return this.certificadoTag?.severity === 'danger';
    }

    get capitalSocialAtualizacao(): AbstractControl | null {
        return this.form.get('capitalSocialAtualizacao');
    }

    get capitalSocialOutraUfAtualizacao(): AbstractControl | null {
        return this.form.get('capitalSocialOutraUfAtualizacao');
    }

    get tipoEstabelecimentoAtual(): string | null {
        return this.form.get('tipoEstabelecimento')?.value ?? null;
    }

    get isTipoFilial(): boolean {
        const tipo = this.tipoEstabelecimentoAtual;
        return tipo === 'S' || tipo === 'C' || tipo === 'O';
    }

    get numeroFiliaisAtual(): number {
        return Number(this.form.get('numeroFiliais')?.value ?? 0) || 0;
    }

    get podeVerFiliais(): boolean {
        return this.numeroFiliaisAtual > 0 || this.tipoEstabelecimentoAtual === 'M';
    }

    get podeZerarFiliais(): boolean {
        return this.tipoEstabelecimentoAtual !== 'M' && this.numeroFiliaisAtual > 0;
    }

    get opcoesSituacaoFiltradas(): OpcaoSelect[] {
        const idTipoInscricao = this.form.get('idTipoInscricao')?.value;
        if (!idTipoInscricao) {
            return [];
        }

        return this.opcoesSituacao.filter((item) => item.group === String(idTipoInscricao));
    }

    get situacaoAtualId(): number | null {
        const valor = this.form.get('idSituacao')?.value;
        return valor != null && valor !== '' ? Number(valor) : null;
    }

    get podeProrrogar(): boolean {
        return !!this.dadosCarregados && !this.carregando() && !this.isEditing && this.tipoInscricaoAtualId === TIPO_INSCRICAO_PROVISORIA && this.situacaoAtualId === SITUACAO_PROVISORIA_PENDENTE;
    }

    get tipoInscricaoAtualId(): number | null {
        const valor = this.form.get('idTipoInscricao')?.value;
        return valor != null && valor !== '' ? Number(valor) : null;
    }

    get alertasAtivos(): string[] {
        const alertas = this.alertas();
        if (!alertas) {
            return [];
        }
        return ALERTAS_PRESTADOR.filter((item) => alertas[item.chave]).map((item) => item.mensagem);
    }

    get diasRestantes(): number | null {
        return this.dadosCarregados?.diasRestantes ?? null;
    }

    get diasRestantesDescricao(): string | null {
        return this.dadosCarregados?.diasRestantesDescricao ?? null;
    }

    get diasRestantesPositivo(): boolean {
        return (this.diasRestantes ?? -1) >= 0;
    }

    /**
     * Aplica o filtro de visibilidade (dbfiltroclassificacao) sobre as classificações:
     * 0 → todas; 1 ("Novas") → as com flag 'N'; 2 ("Transição") → as com flag 'T'.
     * O `group` de cada opção carrega as VIS_FLAGS agregadas no backend ('N', 'T' ou 'NT').
     */
    get opcoesClassificacaoFiltradas(): OpcaoSelect[] {
        const filtro = Number(this.form.get('filtroClassificacao')?.value ?? 0);
        if (filtro === 1) {
            return this.opcoesClassificacao.filter((item) => (item.group ?? '').includes('N'));
        }
        if (filtro === 2) {
            return this.opcoesClassificacao.filter((item) => (item.group ?? '').includes('T'));
        }
        return this.opcoesClassificacao;
    }

    get opcoesClassificacaoDetalheFiltradas(): OpcaoSelect[] {
        const idClassificacao = this.form.get('idClassificacao')?.value;
        if (!idClassificacao) {
            return [];
        }

        return this.opcoesClassificacaoDetalhe.filter((item) => item.parent === String(idClassificacao));
    }

    get opcoesClassificacaoSubtipoFiltradas(): OpcaoSelect[] {
        const detalheSelecionado = this.buscarClassificacaoDetalheSelecionada();
        if (!detalheSelecionado || detalheSelecionado.group !== 'TIPO') {
            return [];
        }

        return this.opcoesClassificacaoSubtipo.filter((item) => item.parent === detalheSelecionado.value);
    }

    @HostListener('window:resize')
    resumirLabels(): void {
        this.isLabelResumo = window.innerWidth <= 1024;
    }

    enableEditing(): void {
        this.isEditing = true;
        this.camposEditaveis.forEach((campo) => this.form.get(campo)?.enable({ emitEvent: false }));
        this.sincronizarCamposPorTipoInscricao();
        this.sincronizarCamposPorClassificacao();
        this.sincronizarCamposPorClassificacaoDetalhe();
        this.sincronizarCamposPorTipoEstabelecimento();
        this.sincronizarBloqueioCrmRJ();
    }

    saveChanges(): void {
        if (this.form.invalid || this.form.pending || !this.pessoa()) {
            this.form.markAllAsTouched();
            return;
        }

        const request = this.construirRequest();
        if (!request) {
            return;
        }

        const situacaoOriginal = this.dadosCarregados?.idSituacao ?? null;
        const novaSituacao = ValueUtils.toNumber(this.form.get('idSituacao')?.value);
        const mudouSituacao = situacaoOriginal != null && novaSituacao != null && novaSituacao !== situacaoOriginal;
        // Data pretendida para a mudança de situação (o formulário é revertido após o update).
        const dataSituacaoPretendida = DateUtils.formatDateToApi(this.form.get('dataSituacao')?.value);

        // A situação, quando alterada, é aplicada pela cascata (PUT /situacao) para preservar o
        // fechamento de vínculos, o bloqueio 5→10, a inadimplência (6/7) e a reativação por grupos.
        // O update de dados gerais mantém a situação/data originais para não movê-la sem os efeitos.
        if (mudouSituacao) {
            request.idSituacao = situacaoOriginal;
            request.dataSituacao = this.dadosCarregados?.dataSituacao ?? request.dataSituacao;
        }

        this.carregando.set(true);
        this.prestadorDadosGeraisService
            .atualizar(request)
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (response) => {
                    this.aplicarDados(response);
                    this.isEditing = false;
                    this.salvo.emit();
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Dados gerais salvos',
                        detail: 'Prestador atualizado com sucesso.'
                    });
                    if (mudouSituacao && novaSituacao != null) {
                        this.abrirCascataSituacao(novaSituacao, dataSituacaoPretendida);
                    }
                },
                // Vazio de proposito: o toast e do interceptor global e o rascunho fica em edicao para o usuario corrigir.
                error: () => {}
            });
    }

    private abrirCascataSituacao(novaSituacao: number, dataSituacao: string | null): void {
        this.situacaoNovaCascata.set(novaSituacao);
        this.dataSituacaoCascata.set(dataSituacao);
        this.situacaoCascataVisivel.set(true);
    }

    onSituacaoCascataConcluida(): void {
        const pessoa = this.pessoa();
        if (pessoa?.id && pessoa?.uf) {
            this.carregarDados(pessoa);
        }
    }

    cancelEditing(): void {
        if (this.dadosCarregados) {
            this.aplicarDados(this.dadosCarregados);
        } else {
            this.limparFormulario();
        }
        this.isEditing = false;
    }

    abrirPesquisaMatriz(): void {
        this.pesquisaMatrizVisivel.set(true);
    }

    selecionarMatriz(pessoa: PessoaDomain): void {
        this.form.patchValue(
            {
                matrizCodigo: pessoa.id != null ? String(pessoa.id) : '',
                matrizUf: pessoa.uf ?? '',
                matrizDescricao: pessoa.nome ?? ''
            },
            { emitEvent: false }
        );
    }

    limparMatriz(): void {
        this.form.patchValue({ matrizCodigo: '', matrizUf: '', matrizDescricao: '' }, { emitEvent: false });
    }

    abrirPesquisaMantenedora(): void {
        this.pesquisaMantenedoraVisivel.set(true);
    }

    selecionarMantenedora(pessoa: PessoaDomain): void {
        this.form.patchValue(
            {
                mantenedoraCodigo: pessoa.id ?? null,
                mantenedoraUf: pessoa.uf ?? '',
                mantenedoraRazaoSocial: pessoa.nome ?? ''
            },
            { emitEvent: false }
        );
    }

    limparMantenedora(): void {
        this.form.patchValue({ mantenedoraCodigo: null, mantenedoraUf: '', mantenedoraRazaoSocial: '' }, { emitEvent: false });
    }

    abrirProrrogarPrazo(): void {
        if (!this.podeProrrogar) {
            return;
        }
        this.prorrogarPrazoVisivel.set(true);
    }

    onPrazoProrrogado(): void {
        const pessoa = this.pessoa();
        if (pessoa?.id && pessoa?.uf) {
            this.carregarDados(pessoa);
        }
    }

    abrirFiliais(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf) {
            return;
        }

        this.filiaisDialogVisivel.set(true);
        this.carregandoFiliais.set(true);
        this.prestadorDadosGeraisService
            .listarFiliais(pessoa)
            .pipe(
                finalize(() => this.carregandoFiliais.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (filiais) => this.filiais.set(filiais ?? []),
                error: () => this.filiais.set([])
            });
    }

    abrirPrestador(filial: PrestadorFilialResponse): void {
        if (!filial.idSolicitante || !filial.uf) {
            return;
        }
        // Filial é outro prestador; abre o dashboard dele mantendo o botão global de voltar.
        // Caso-limite: origem e destino são ambos /prestadores/dados-gerais, então o retorno só
        // restaura o prestador anterior ao clicar "Voltar" (não ao navegar entre abas do alvo).
        this.aberturaNavigationService.abrirPrestador(
            this.pessoa() as PessoaDomain | undefined,
            { id: filial.idSolicitante, uf: filial.uf, crm: filial.crm ?? undefined, nome: filial.razaoSocial ?? filial.nomeFantasia ?? undefined, tipo: 'PRESTADOR' },
            '/prestadores/dados-gerais'
        );
        this.filiaisDialogVisivel.set(false);
    }

    abrirHistoricoCapitalSocial(tipo: 'P' | 'M'): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf) {
            return;
        }

        this.capitalHistoricoTipo.set(tipo);
        this.capitalHistorico.set([]);
        this.capitalHistoricoDialogVisivel.set(true);
        this.carregandoCapitalHistorico.set(true);
        this.prestadorDadosGeraisService
            .listarHistoricoCapitalSocial(pessoa, tipo)
            .pipe(
                finalize(() => this.carregandoCapitalHistorico.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (historico) => this.capitalHistorico.set(historico ?? []),
                error: () => this.capitalHistorico.set([])
            });
    }

    abrirHistoricoCertificado(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf) {
            return;
        }

        this.certificadoHistorico.set([]);
        this.certificadoHistoricoDialogVisivel.set(true);
        this.carregandoCertificadoHistorico.set(true);
        this.prestadorDadosGeraisService
            .listarHistoricoCertificado(pessoa)
            .pipe(
                finalize(() => this.carregandoCertificadoHistorico.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (historico) => this.certificadoHistorico.set(historico ?? []),
                error: () => this.certificadoHistorico.set([])
            });
    }

    salvarRenovacaoCertificado(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf || this.renovacaoForm.invalid) {
            this.renovacaoForm.markAllAsTouched();
            return;
        }

        const raw = this.renovacaoForm.getRawValue();
        this.salvandoRenovacao.set(true);
        this.prestadorDadosGeraisService
            .renovarCertificado({
                id: pessoa.id,
                uf: pessoa.uf,
                dtRenovacao: DateUtils.formatDateToApi(raw.dtRenovacao)!
            })
            .pipe(
                finalize(() => this.salvandoRenovacao.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificacoesService.notificar({ severity: 'success', summary: 'Renovação do certificado salva.' });
                    this.renovacaoForm.reset({ dtRenovacao: new Date() });
                    this.abrirHistoricoCertificado();
                }
            });
    }

    confirmarZerarFiliais(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf || !this.podeZerarFiliais) {
            return;
        }

        this.confirmationService.confirm({
            header: 'Atenção',
            message: 'Deseja realmente excluir a(s) filial(ais) vinculadas?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => this.zerarFiliais(pessoa)
        });
    }

    private zerarFiliais(pessoa: Pessoa): void {
        this.zerandoFiliais.set(true);
        this.prestadorDadosGeraisService
            .zerarFiliais(pessoa)
            .pipe(
                finalize(() => this.zerandoFiliais.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Filiais desvinculadas',
                        detail: 'Filial(ais) excluída(s) com sucesso!'
                    });
                    this.carregarDados(pessoa);
                },
                // Vazio de proposito: o toast e do interceptor global e o rascunho fica em edicao para o usuario corrigir.
                error: () => {}
            });
    }

    private sincronizarBloqueioCrmRJ(): void {
        if (this.crmBloqueadoPorRJ) {
            this.crm?.disable({ emitEvent: false });
        }
    }

    private crmDisponibilidadeValidator(): AsyncValidatorFn {
        return (control: AbstractControl): ReturnType<AsyncValidatorFn> => {
            if (!this.isEditing || this.crmBloqueadoPorRJ) {
                return of(null);
            }

            const valor = ValueUtils.normalizeText(control.value);
            if (!valor || control.hasError('pattern') || this.crmIgualAoOriginal(valor)) {
                this.crmDisponibilidade.set(null);
                this.crmVerificando.set(false);
                return of(null);
            }

            const pessoa = this.pessoa();
            if (!pessoa) {
                return of(null);
            }

            this.crmVerificando.set(true);
            return timer(CRM_VERIFICACAO_DEBOUNCE_MS).pipe(
                switchMap(() => this.prestadorDadosGeraisService.verificarCrmDisponivel({ id: pessoa.id, uf: pessoa.uf, crm: valor })),
                map((response: PrestadorCrmDisponivelResponse): ValidationErrors | null => {
                    this.crmVerificando.set(false);
                    this.crmDisponibilidade.set(response.disponivel ? 'disponivel' : 'indisponivel');
                    return response.disponivel ? null : { crmEmUso: true };
                }),
                catchError(() => {
                    this.crmVerificando.set(false);
                    this.crmDisponibilidade.set(null);
                    return of(null);
                })
            );
        };
    }

    private crmIgualAoOriginal(valor: string): boolean {
        const original = this.dadosCarregados?.crm ?? '';
        return this.normalizarCrmParaComparacao(valor) === this.normalizarCrmParaComparacao(original);
    }

    private normalizarCrmParaComparacao(valor: string): string {
        const digitos = (valor ?? '').replace(/\D/g, '');
        return digitos.replace(/^0+(?=\d)/, '');
    }

    private configurarRegrasFormulario(): void {
        this.form
            .get('idTipoInscricao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorTipoInscricao());

        this.form
            .get('idClassificacao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorClassificacao());

        this.form
            .get('classificacaoDetalhe')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorClassificacaoDetalhe());

        this.form
            .get('tipoEstabelecimento')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorTipoEstabelecimento());

        // Ao editar o valor do capital, carimba a Dt. Atualizacao com hoje, para que cada edicao
        // gere uma nova entrada no historico (FAIXA_CAPITAL_SOCIAL), como o legado (INSERT SYSDATE).
        this.form
            .get('capitalSocial')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.carimbarDataCapital('capitalSocialAtualizacao'));

        this.form
            .get('capitalSocialOutraUf')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.carimbarDataCapital('capitalSocialOutraUfAtualizacao'));
    }

    /**
     * Carimba o campo de data de atualizacao do capital com a data de hoje quando o operador edita
     * o valor. Assim cada edicao vira uma nova linha no historico de capital social (por dia).
     */
    private carimbarDataCapital(campoData: 'capitalSocialAtualizacao' | 'capitalSocialOutraUfAtualizacao'): void {
        if (!this.isEditing) {
            return;
        }
        this.form.get(campoData)?.setValue(new Date(), { emitEvent: false });
    }

    /** Pede a recarga. Sem guard: quem chama de dentro de outro subscribe e atendido igual. */
    private carregarDados(pessoa: Pessoa): void {
        this.recarregar$.next(pessoa);
    }

    private carregarAlertas(pessoa: Pessoa): void {
        this.prestadorAlertasService
            .buscar(pessoa)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (alertas) => this.alertas.set(alertas),
                error: () => this.alertas.set(null)
            });
    }

    private aplicarDados(response: PrestadorDadosGeraisResponse): void {
        this.dadosCarregados = response;
        this.crmVerificando.set(false);
        this.crmDisponibilidade.set(null);
        this.definirOpcoes(response.referencias);

        this.form.patchValue(
            {
                crm: response.crm ?? '',
                uf: response.uf ?? '',
                cls: response.cls != null ? String(response.cls) : '',
                razaoSocial: response.razaoSocial ?? '',
                dataInscricao: DateUtils.parseToDate(response.dataInscricao),
                nomeFantasia: response.nomeFantasia ?? '',
                nomeUsual: response.nomeUsual ?? '',
                modalidade: ValueUtils.toSelectValue(response.modalidade),
                idTipoInscricao: ValueUtils.toSelectValue(response.idTipoInscricao),
                idSituacao: ValueUtils.toSelectValue(response.idSituacao),
                dataSituacao: DateUtils.parseToDate(response.dataSituacao),
                filtroClassificacao: this.resolverFiltroClassificacaoPadrao(response.classificacaoVisibilidade),
                idClassificacao: ValueUtils.toSelectValue(response.idClassificacao),
                classificacaoDetalhe: this.resolverClassificacaoDetalhe(response),
                idClassificacaoSubtipo: ValueUtils.toSelectValue(response.idClassificacaoSubtipo),
                especialidadeEnfase: response.especialidadeEnfase ?? '',
                categoria: ValueUtils.toSelectValue(response.categoria),
                dataRenovacao: DateUtils.parseToDate(response.dataRenovacao),
                validadeCertificado: DateUtils.parseToDate(response.validadeCertificado),
                tipoEstabelecimento: ValueUtils.toSelectValue(response.tipoEstabelecimento),
                capitalSocial: response.capitalSocial ?? null,
                capitalSocialAtualizacao: DateUtils.parseToDate(response.capitalSocialAtualizacao),
                capitalSocialOutraUf: response.capitalSocialOutraUf ?? null,
                capitalSocialOutraUfAtualizacao: DateUtils.parseToDate(response.capitalSocialOutraUfAtualizacao),
                numeroFiliais: response.numeroFiliais ?? null,
                matrizCodigo: response.matrizCodigo != null ? String(response.matrizCodigo) : '',
                matrizUf: response.matrizUf ?? '',
                matrizDescricao: response.matrizDescricao ?? '',
                mantenedoraCodigo: response.mantenedoraCodigo ?? null,
                mantenedoraUf: response.mantenedoraUf ?? '',
                mantenedoraRazaoSocial: response.mantenedoraRazaoSocial ?? '',
                naturezaJuridicaMantenedor: response.naturezaJuridicaMantenedor ?? null
            },
            { emitEvent: false }
        );

        this.sincronizarCamposPorTipoInscricao();
        this.sincronizarCamposPorClassificacao();
        this.sincronizarCamposPorClassificacaoDetalhe();
        this.sincronizarCamposPorTipoEstabelecimento();
        this.form.disable({ emitEvent: false });
    }

    private limparFormulario(): void {
        this.dadosCarregados = null;
        this.crmVerificando.set(false);
        this.crmDisponibilidade.set(null);
        this.alertas.set(null);
        this.form.reset(
            {
                crm: '',
                uf: '',
                cls: '',
                razaoSocial: '',
                dataInscricao: null,
                nomeFantasia: '',
                nomeUsual: '',
                modalidade: null,
                idTipoInscricao: null,
                idSituacao: null,
                dataSituacao: null,
                filtroClassificacao: 0,
                idClassificacao: null,
                classificacaoDetalhe: null,
                idClassificacaoSubtipo: null,
                especialidadeEnfase: '',
                categoria: null,
                dataRenovacao: null,
                validadeCertificado: null,
                tipoEstabelecimento: null,
                capitalSocial: null,
                capitalSocialAtualizacao: null,
                capitalSocialOutraUf: null,
                capitalSocialOutraUfAtualizacao: null,
                numeroFiliais: null,
                matrizCodigo: '',
                matrizUf: '',
                matrizDescricao: '',
                mantenedoraCodigo: null,
                mantenedoraUf: '',
                mantenedoraRazaoSocial: '',
                naturezaJuridicaMantenedor: null
            },
            { emitEvent: false }
        );
        this.form.disable({ emitEvent: false });
    }

    private definirOpcoes(referencias?: PrestadorDadosGeraisReferenciasResponse | null): void {
        this.opcoesModalidade = this.mapearOpcoes(referencias?.modalidades);
        this.opcoesTipoInscricao = this.mapearOpcoes(referencias?.tiposInscricao);
        this.opcoesSituacao = this.mapearOpcoes(referencias?.situacoes);
        this.opcoesClassificacao = this.mapearOpcoes(referencias?.classificacoes);
        this.opcoesClassificacaoDetalhe = this.mapearOpcoes(referencias?.classificacaoDetalhes);
        this.opcoesClassificacaoSubtipo = this.mapearOpcoes(referencias?.classificacaoSubtipos);
        this.opcoesCategoria = this.mapearOpcoes(referencias?.categorias);
        this.opcoesTipoEstabelecimento = this.mapearOpcoes(referencias?.tiposEstabelecimento);
    }

    private mapearOpcoes(opcoes?: PrestadorDadosGeraisOpcaoResponse[] | null): OpcaoSelect[] {
        return (opcoes ?? []).map((item) => ({
            label: item.descricao,
            value: item.valor,
            group: item.grupo,
            parent: item.pai
        }));
    }

    private sincronizarCamposPorTipoInscricao(): void {
        const situacaoControl = this.form.get('idSituacao');
        const situacaoAtual = situacaoControl?.value;
        const situacaoValida = this.opcoesSituacaoFiltradas.some((item) => item.value === situacaoAtual);

        if (!situacaoValida) {
            situacaoControl?.setValue(null, { emitEvent: false });
        }
    }

    private sincronizarCamposPorClassificacao(): void {
        const detalheControl = this.form.get('classificacaoDetalhe');
        const detalheAtual = detalheControl?.value;
        const detalheValido = this.opcoesClassificacaoDetalheFiltradas.some((item) => item.value === detalheAtual);

        if (!detalheValido) {
            detalheControl?.setValue(null, { emitEvent: false });
        }

        this.sincronizarCamposPorClassificacaoDetalhe();
    }

    private sincronizarCamposPorClassificacaoDetalhe(): void {
        const subtipoControl = this.form.get('idClassificacaoSubtipo');
        const detalheSelecionado = this.buscarClassificacaoDetalheSelecionada();
        const subtipoAtual = subtipoControl?.value;
        const subtipoValido = this.opcoesClassificacaoSubtipoFiltradas.some((item) => item.value === subtipoAtual);

        if (!subtipoValido) {
            subtipoControl?.setValue(null, { emitEvent: false });
        }

        if (!this.isEditing) {
            return;
        }

        if (detalheSelecionado?.group === 'TIPO') {
            subtipoControl?.enable({ emitEvent: false });
            return;
        }

        subtipoControl?.disable({ emitEvent: false });
    }

    private sincronizarCamposPorTipoEstabelecimento(): void {
        const tipoEstabelecimento = this.form.get('tipoEstabelecimento')?.value;
        const capitalOutraUf = this.form.get('capitalSocialOutraUf');
        const capitalOutraUfAtualizacao = this.form.get('capitalSocialOutraUfAtualizacao');

        if (!this.isEditing) {
            return;
        }

        if (tipoEstabelecimento === 'O') {
            capitalOutraUf?.enable({ emitEvent: false });
            capitalOutraUfAtualizacao?.enable({ emitEvent: false });
            return;
        }

        capitalOutraUf?.disable({ emitEvent: false });
        capitalOutraUfAtualizacao?.disable({ emitEvent: false });
    }

    private construirRequest(): PrestadorDadosGeraisRequest | null {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return null;
        }

        const detalheSelecionado = this.buscarClassificacaoDetalheSelecionada();
        const rawValue = this.form.getRawValue();

        return {
            id: pessoa.id,
            uf: pessoa.uf,
            crm: ValueUtils.normalizeText(rawValue.crm),
            razaoSocial: ValueUtils.normalizeText(rawValue.razaoSocial),
            nomeFantasia: ValueUtils.normalizeText(rawValue.nomeFantasia),
            nomeUsual: ValueUtils.normalizeText(rawValue.nomeUsual),
            modalidade: ValueUtils.normalizeText(rawValue.modalidade),
            idTipoInscricao: ValueUtils.toNumber(rawValue.idTipoInscricao),
            idSituacao: ValueUtils.toNumber(rawValue.idSituacao),
            dataInscricao: DateUtils.formatDateToApi(rawValue.dataInscricao),
            dataSituacao: DateUtils.formatDateToApi(rawValue.dataSituacao),
            idClassificacao: ValueUtils.toNumber(rawValue.idClassificacao),
            idClassificacaoPorte: detalheSelecionado?.group === 'PORTE' ? ValueUtils.toNumber(detalheSelecionado.value) : null,
            idClassificacaoTipo: detalheSelecionado?.group === 'TIPO' ? ValueUtils.toNumber(detalheSelecionado.value) : null,
            idClassificacaoSubtipo: detalheSelecionado?.group === 'TIPO' ? ValueUtils.toNumber(rawValue.idClassificacaoSubtipo) : null,
            categoria: ValueUtils.normalizeText(rawValue.categoria),
            tipoEstabelecimento: ValueUtils.normalizeText(rawValue.tipoEstabelecimento),
            capitalSocial: ValueUtils.toDecimal(rawValue.capitalSocial),
            capitalSocialAtualizacao: DateUtils.formatDateToApi(rawValue.capitalSocialAtualizacao),
            capitalSocialOutraUf: rawValue.tipoEstabelecimento === 'O' ? ValueUtils.toDecimal(rawValue.capitalSocialOutraUf) : null,
            capitalSocialOutraUfAtualizacao: rawValue.tipoEstabelecimento === 'O' ? DateUtils.formatDateToApi(rawValue.capitalSocialOutraUfAtualizacao) : null,
            numeroFiliais: ValueUtils.toNumber(rawValue.numeroFiliais),
            matrizCodigo: this.isTipoEstabelecimentoFilial(rawValue.tipoEstabelecimento) ? ValueUtils.toNumber(rawValue.matrizCodigo) : null,
            matrizUf: this.isTipoEstabelecimentoFilial(rawValue.tipoEstabelecimento) ? this.normalizarUf(rawValue.matrizUf) : null,
            mantenedoraCodigo: ValueUtils.toNumber(rawValue.mantenedoraCodigo),
            mantenedoraUf: this.normalizarUf(rawValue.mantenedoraUf),
            naturezaJuridicaMantenedor: ValueUtils.normalizeText(rawValue.naturezaJuridicaMantenedor)
        };
    }

    private isTipoEstabelecimentoFilial(tipo: unknown): boolean {
        return tipo === 'S' || tipo === 'C' || tipo === 'O';
    }

    private normalizarUf(uf: unknown): string | null {
        const texto = ValueUtils.normalizeText(uf);
        return texto ? texto.toUpperCase() : null;
    }

    private buscarClassificacaoDetalheSelecionada(): OpcaoSelect | undefined {
        const valorSelecionado = this.form.get('classificacaoDetalhe')?.value;
        return this.opcoesClassificacaoDetalheFiltradas.find((item) => item.value === valorSelecionado);
    }

    /**
     * Valor padrão do filtro de classificação a partir da visibilidade da classificação atual,
     * fiel ao legado (UntCadPrestador.pas:1812-1818): null/vazio → 1 ("Novas"); 'S'/'N' → 2 ("Transição");
     * demais (T) → 0 ("Todas").
     */
    private resolverFiltroClassificacaoPadrao(visibilidade?: string | null): number {
        const valor = (visibilidade ?? '').trim();
        if (!valor) {
            return 1;
        }
        if (valor === 'S' || valor === 'N') {
            return 2;
        }
        return 0;
    }

    private resolverClassificacaoDetalhe(response: PrestadorDadosGeraisResponse): string | null {
        if (response.idClassificacaoTipo) {
            return String(response.idClassificacaoTipo);
        }
        if (response.idClassificacaoPorte) {
            return String(response.idClassificacaoPorte);
        }
        return null;
    }
}
